import { useCallback } from "react";
import { NavLink, Outlet, useMatch, useOutletContext, useParams } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { ErrorState, LoadingBlock } from "../components/Feedback";
import { Icon } from "../components/Icon";
import { SandboxDockProvider } from "../components/SandboxDock";
import { useSession } from "../lib/session";
import { api, loadModuleContent } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { formatMinutes, formatPercent } from "../lib/format";
import type { ModuleContent, ModuleDetailProgress } from "../lib/types";
import NotFound from "./NotFound";

export interface ModuleCtx {
  content: ModuleContent;
  progress: ModuleDetailProgress;
  reloadModuleProgress: () => Promise<void>;
}

export function useModuleCtx(): ModuleCtx {
  return useOutletContext<ModuleCtx>();
}

/**
 * Loads a module's compiled content and the learner's progress through it, then frames the
 * reader with the lessons → quiz → lab rail.
 */
export default function ModuleShell() {
  const { moduleId = "" } = useParams();
  const { settings } = useSession();
  // The lab page has its own terminal; everywhere else in a module, the sandbox is a click away.
  const onLab = useMatch("/modules/:moduleId/lab") !== null;

  const content = useAsync(() => loadModuleContent(moduleId), [moduleId]);
  const progress = useAsync((signal) => api.moduleProgress(moduleId, signal), [moduleId]);

  const reloadModuleProgress = useCallback(async () => {
    await progress.reload();
  }, [progress]);

  if (content.error || progress.error) {
    const error = content.error ?? progress.error;
    const notFound =
      typeof error === "object"
      && error !== null
      && "status" in error
      && (error as { status: number }).status === 404;
    if (notFound) return <NotFound />;
    return (
      <AppShell>
        <ErrorState
          error={error}
          onRetry={() => {
            void content.reload();
            void progress.reload();
          }}
          title="This module did not load"
        />
      </AppShell>
    );
  }

  if (!content.data || !progress.data) {
    return (
      <AppShell>
        <LoadingBlock label="Loading the module" />
      </AppShell>
    );
  }

  const { module, lessons, quiz, lab } = content.data;
  const lessonState = progress.data.lessons;
  const base = `/modules/${module.id}`;

  return (
    <AppShell>
      <SandboxDockProvider available={settings.sandboxEnabled && !onLab}>
        <div className="module-view">
          <nav className="module-nav" aria-label="Module contents">
            <div className="module-nav-head">
              <p className="mono">
                <NavLink to={`/courses/${module.courseId}`} className="module-nav-course">
                  {module.courseTitle}
                </NavLink>{" "}
                · Module {String(module.number).padStart(2, "0")} — {module.level}
              </p>
              <h1>{module.title}</h1>
              <NavLink to={base} end className="btn btn-sm">
                Module overview
              </NavLink>
            </div>

            <div className="lesson-rail">
              <p className="rail-divider">Lessons</p>
              {lessons.map((lesson, index) => {
                const done = !!lessonState[lesson.id];
                return (
                  <NavLink
                    key={lesson.id}
                    to={`${base}/lessons/${lesson.id}`}
                    className={({ isActive }) =>
                      [done ? "is-done" : "", isActive ? "is-current" : ""].filter(Boolean).join(" ")}
                  >
                    <Icon
                      name={done ? "check-circle" : "circle"}
                      size={15}
                      className="rail-glyph"
                      label={done ? "Completed" : undefined}
                    />
                    <span>
                      {index + 1}. {lesson.title}
                      <span className="rail-minutes">{formatMinutes(lesson.minutes)}</span>
                    </span>
                  </NavLink>
                );
              })}

              <p className="rail-divider">Check your understanding</p>
              <NavLink
                to={`${base}/quiz`}
                className={({ isActive }) =>
                  [progress.data?.quiz?.passed ? "is-done" : "", isActive ? "is-current" : ""]
                    .filter(Boolean).join(" ")}
              >
                <Icon
                  name={progress.data.quiz?.passed ? "check-circle" : "circle"}
                  size={15}
                  className="rail-glyph"
                />
                <span>
                  {quiz.title.replace(/\s*quiz$/i, "")} quiz
                  <span className="rail-minutes">
                    {progress.data.quiz
                      ? `best ${formatPercent(progress.data.quiz.bestScore)} of ${formatPercent(quiz.passMark)} needed`
                      : `${quiz.questions.length} questions, ${formatPercent(quiz.passMark)} to pass`}
                  </span>
                </span>
              </NavLink>

              <p className="rail-divider">Hands on</p>
              <NavLink
                to={`${base}/lab`}
                className={({ isActive }) =>
                  [progress.data?.lab.status === "passed" ? "is-done" : "", isActive ? "is-current" : ""]
                    .filter(Boolean).join(" ")}
              >
                <Icon
                  name={progress.data.lab.status === "passed" ? "check-circle" : "terminal"}
                  size={15}
                  className="rail-glyph"
                />
                <span>
                  {lab.title}
                  <span className="rail-minutes">
                    {progress.data.lab.status === "passed"
                      ? `passed ${progress.data.lab.bestScore}/${progress.data.lab.maxScore}`
                      : `${formatMinutes(lab.minutes)}, ${lab.maxScore} points`}
                  </span>
                </span>
              </NavLink>
            </div>
          </nav>

          <Outlet
            context={{
              content: content.data,
              progress: progress.data,
              reloadModuleProgress,
            } satisfies ModuleCtx}
          />
        </div>
      </SandboxDockProvider>
    </AppShell>
  );
}
